import moment from 'moment';
import {
  generatePdfFromTemplate,
  PdfTemplateLayoutItem,
  GeneratePdfTemplateResult,
  PdfTemplateFontWeight,
} from './PdfTemplateService';

const VERBAL_WARNING_PDF_URL =
  'https://arb-systems-s3-public.s3.amazonaws.com/public/Performance/PerformanceManagement_VerbalWarning.pdf';

const formatDateValue = (value?: string) => {
  if (!value) {
    return '';
  }

  const parsed = moment(value);
  if (!parsed.isValid()) {
    return value;
  }

  return parsed.format('MM/DD/YYYY');
};

const extractField = (data: any, candidates: string[]) => {
  for (const key of candidates) {
    const segments = key.split('.');
    let current = data;

    for (const segment of segments) {
      if (current && Object.prototype.hasOwnProperty.call(current, segment)) {
        current = current[segment];
      } else {
        current = undefined;
        break;
      }
    }

    if (current !== undefined && current !== null) {
      if (typeof current === 'string') {
        const trimmed = current.trim();
        if (trimmed !== '') {
          return trimmed;
        }
      }

      if (typeof current === 'number') {
        return current.toString();
      }

      if (current instanceof Date) {
        return moment(current).format('MM/DD/YYYY');
      }
    }
  }

  return '';
};

const getPolicyViolationText = (item: any): string => {
  if (item?.policyViolations && Array.isArray(item.policyViolations) && item.policyViolations.length > 0) {
    return item.policyViolations
      .map((pv: any) => (typeof pv === 'string' ? pv : pv?.name ?? ''))
      .filter(Boolean)
      .join(', ');
  }
  return extractField(item, ['policy_violation', 'policyViolation', 'policyViolation.name', 'policy_violated']) || '';
};

const sanitizeFileName = (text: string) => {
  if (!text) {
    return 'verbal-warning';
  }

  return (
    text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .substring(0, 80) || 'verbal-warning'
  );
};

interface AddTextOptions {
  y?: number;
  x?: number;
  textColor?: string;
  page?: number;
  fontSize?: number;
  fontWeight?: PdfTemplateFontWeight;
  maxWidth?: number;
  lineHeight?: number;
  spacingAfter?: number;
}

const buildVerbalWarningLayout = (item: any, userLogged?: any, currentSchool?: any) => {
  const employeeName = extractField(item, ['name', 'employeeName', 'employee_name', 'employee.name']) || '';
  const position = extractField(item, ['position', 'employeePosition', 'jobTitle', 'job_title']) || '';
  const department = extractField(item, ['department', 'department.name', 'departmentName']) || '';
  const supervisor = extractField(item, ['supervisor', 'supervisor.name', 'supervisorName', 'supervisor_name']) || '';
  const dateOfIncident = formatDateValue(extractField(item, ['date', 'dateOfIncident', 'date_of_incident']));
  const dateOfHire = formatDateValue(extractField(item, ['date_of_hire', 'dateOfHire']));
  const policyViolation = getPolicyViolationText(item);
  const reasonForVerbalWarning = extractField(item, ['reason_for_verbal_warning', 'reasonForVerbalWarning', 'reason']);
  const nextConsequences = extractField(item, ['next_consequences', 'nextConsequences']);
  const expectationOfImprovement = extractField(item, [
    'expectation_of_improvement',
    'expectationOfImprovement',
    'expectations',
  ]);
  const createdAt = formatDateValue(extractField(item, ['created_at', 'createdAt']));
  const _programName = extractField(item, ['programName', 'program_name']);

  const layout: PdfTemplateLayoutItem[] = [];
  const addText = (text: string, options: AddTextOptions = {}) => {
    const { x, ...rest } = options;
    const textItem: PdfTemplateLayoutItem = {
      type: 'text',
      text,
      x: x ?? 54,
      ...rest,
    };
    layout.push(textItem);
  };

  addText(`${currentSchool?.label ?? ''}`, {
    fontWeight: 'regular',
    fontSize: 16,
    textColor: '#fd9821',
    y: 711,
    x: 203,
  });

  const submittedByName = item?.submittedByUser
    ? `${item.submittedByUser.firstName ?? ''} ${item.submittedByUser.lastName ?? ''}`.trim()
    : '';

  addText(submittedByName, {
    fontWeight: 'regular',
    fontSize: 12,
    y: 622,
    x: 150,
  });

  addText(`${formatDateValue(extractField(item, ['created_at', 'createdAt']))}`, {
    fontWeight: 'regular',
    fontSize: 12,
    y: 605,
    x: 157,
  });

  addText(`${employeeName}`, {
    fontWeight: 'regular',
    fontSize: 12,
    y: 567,
    x: 110,
  });

  addText(`${dateOfIncident}`, {
    fontWeight: 'regular',
    fontSize: 12,
    y: 567,
    x: 400,
  });

  addText(`${position}`, {
    fontWeight: 'regular',
    fontSize: 12,
    y: 544,
    x: 120,
  });

  addText(`${department}`, {
    fontWeight: 'regular',
    fontSize: 12,
    y: 544,
    x: 355,
  });

  const sequentialX = 80;
  const sequentialFontSize = 12;
  const spacingAfterTitle = 10;
  const spacingAfterContent = 22;

  addText('Next Consequences:', {
    fontWeight: 'bold',
    fontSize: sequentialFontSize,
    x: sequentialX,
    spacingAfter: spacingAfterTitle,
  });
  addText(`${nextConsequences ?? ''}`, {
    fontWeight: 'regular',
    fontSize: sequentialFontSize,
    x: sequentialX,
    spacingAfter: spacingAfterContent,
  });

  addText('Policy Violation:', {
    fontWeight: 'bold',
    fontSize: sequentialFontSize,
    x: sequentialX,
    spacingAfter: spacingAfterTitle,
  });
  addText(`${policyViolation ?? ''}`, {
    fontWeight: 'regular',
    fontSize: sequentialFontSize,
    x: sequentialX,
    spacingAfter: spacingAfterContent,
  });

  addText('Reason for Verbal Warning:', {
    fontWeight: 'bold',
    fontSize: sequentialFontSize,
    x: sequentialX,
    spacingAfter: spacingAfterTitle,
  });
  addText(`${reasonForVerbalWarning ?? ''}`, {
    fontWeight: 'regular',
    fontSize: sequentialFontSize,
    x: sequentialX,
    spacingAfter: spacingAfterContent,
  });

  addText('Expectation of Improvement:', {
    fontWeight: 'bold',
    fontSize: sequentialFontSize,
    x: sequentialX,
    spacingAfter: spacingAfterTitle,
  });
  addText(`${expectationOfImprovement ?? ''}`, {
    fontWeight: 'regular',
    fontSize: sequentialFontSize,
    x: sequentialX,
    spacingAfter: spacingAfterContent,
  });

  return {
    layout,
    employeeName,
    helpers: {
      addText,
      extractField,
      formatDateValue,
    },
    context: {
      item,
      userLogged,
      currentSchool,
      position,
      department,
      supervisor,
      dateOfIncident,
      dateOfHire,
      policyViolation,
      reasonForVerbalWarning,
      nextConsequences,
      expectationOfImprovement,
      createdAt,
    },
  };
};

export interface VerbalWarningPdfResult extends GeneratePdfTemplateResult {
  fileName: string;
}

export const generateVerbalWarningPdf = async (
  item: any,
  userLogged?: any,
  currentSchool?: any,
): Promise<VerbalWarningPdfResult> => {
  const { layout, employeeName } = buildVerbalWarningLayout(item, userLogged, currentSchool);
  const result = await generatePdfFromTemplate({
    templatePath: VERBAL_WARNING_PDF_URL,
    layout,
    cursorStart: 730,
    templateLastPageIndex: 1,
  });

  return {
    ...result,
    fileName: `${sanitizeFileName(employeeName)}-verbal-warning.pdf`,
  };
};
